import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import DataTable, { SortOrder, TableColumn } from 'react-data-table-component';
import { Tooltip } from '@mui/material';
import { MasterLayout, ToolbarLayout, ToolbarLayoutLeft, ToolbarLayoutRight, MainLayout } from 'common/imports/content-layout';
import { tableHelper } from 'common/helpers/table/table-helper';
import { itLabTableStyle } from 'common/helpers/table/table-style';
import { formatLocalizedDateTime } from 'common/helpers/dateUtils';
import { PagingOptionsModel } from 'common/types/paging-options.model';
import { getStatusColor, getStatusText, getTimeLineBadge } from 'app/dashboard/utils/dashboardUtils';
import { AgentLogStatus } from 'app/dashboard/model/dashboard.model';
import { RobotExecutions, RobotExecutionsErrorReason, RobotExecutionsSuspectReason } from '../models/robot-executions.model';
import { RobotExecutionsService } from '../services/robot-executions.service';
import en from '../i18n/en-us.json';
import pt from '../i18n/pt-br.json';
import es from '../i18n/es-es.json';
import { RobotExecutionsFilterDropdown } from './RobotExecutionsFilterDropdown';
import ErrorDescriptionModal from './ErrorDescriptionModal';
import SuspectDescriptionModal from './SuspectDescriptionModal';
import { UserMessages } from 'common/enums/UserMessages';
import { useStepExecutionFilterParams } from '../hooks/use-filter-params';
import { useWebSocket } from 'common/helpers/signalR/context/signalR';


export default function RobotExecutionsList() {
    i18next.addResourceBundle('us', 'translation', en);
    i18next.addResourceBundle('br', 'translation', pt);
    i18next.addResourceBundle('es', 'translation', es);

    const { t } = useTranslation();

    const [filter, filterActions] = useStepExecutionFilterParams();

    const [tableData, setTableData] = useState<RobotExecutions[]>([]);
    const connection = useWebSocket();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isErrorDescriptionModalOpened, setIsErrorDescriptionModalOpened] = useState<boolean>(false);
    const [errorReasonSelected, setErrorReasonSelected] = useState<RobotExecutionsErrorReason>();

    const [suspectLogSelect, setSuspectLogSelected] = useState<{ id: number, suspectReason?: RobotExecutionsSuspectReason, errorReason?: RobotExecutionsErrorReason }>({ id: 0 });

    const [totalRows, setTotalRows] = useState<number>(0);

    const [sorting, setSorting] = useState<PagingOptionsModel>({
        page: 0,
        limit: tableHelper.defaultRowsPerPage
    });

    const fetchData = useCallback(async () => {
        setIsLoading(true);

        const response = await RobotExecutionsService.getExecutionLog({
            page: sorting.page,
            limit: sorting.limit,
            sort: sorting?.sort,
            order: sorting?.order,
            status: filter?.status,
            errorReason: filter?.errorReason,
            departmentId: filter?.departmentId,
            machineId: filter?.machineId,
            stepId: filter?.stepId
        });

        if (response && !response.hasErrors) {
            setTableData(response.data);
            setTotalRows(response.totalItems);
        }

        setIsLoading(false);
    }, [filter, sorting]);


    useEffect(() => {
        fetchData();
        if (!connection) return;
        const listeners: Function[] = [];
        listeners.push(connection.onLoadLogs(fetchData));
        listeners.push(connection.onAgentConnected(fetchData));
        listeners.push(connection.onAgentDisconnected(fetchData));
        return () => listeners.forEach(l => l())
    }, [connection, fetchData]);

    const shouldRenderSuspectDialogButton = (log: RobotExecutions) => log.status == AgentLogStatus.Suspect || log.suspectReason != null;
    const shouldRenderErrorDialogButton = (log: RobotExecutions) => log.status == AgentLogStatus.Error && log.suspectReason == null;

    const buildTooltipContent = (custom: string | null): JSX.Element[] | null => {
        if (!custom) {
            return null;
        }

        let content: JSX.Element[] = [];

        try {
            if (custom) {
                const jsonObject = JSON.parse(custom);

                Object.keys(jsonObject).forEach((key) => {
                    const normalizedKey = key.toLowerCase();
                    if (normalizedKey === 'endprocess' || normalizedKey === 'restartprocess') {
                        delete jsonObject[key];
                    }
                });

                content = Object.keys(jsonObject).map((key, index) => (
                    <div className='fs-8 fw-bold my-2 me-3 d-block' key={index}>{key}: {jsonObject[key]}</div>
                ));
            }
        } catch (error) {
            content.push(<div className='fs-8 fw-bold my-2 me-3 d-block' key='error'>?: ?</div>);
        }

        return content;
    };

    const openErrorDescriptionModal = (agenteLogId: number, errorReason?: RobotExecutionsErrorReason) => {
        if (!errorReason) {
            errorReason = {
                id: 0,
                agentLogId: agenteLogId,
                errorReasonId: 0,
                name: "",
                reason: ""
            }
        }
        setErrorReasonSelected(errorReason);
    }

    const openSuspectDescriptionModal = (agentLog: RobotExecutions) => {
        setSuspectLogSelected({ id: agentLog.id, suspectReason: agentLog.suspectReason, errorReason: agentLog.errorReason });
    };

    const closeDescriptionModal = () => {
        setErrorReasonSelected(undefined);
        setSuspectLogSelected({ id: 0 });
        fetchData();
    }

    useEffect(() => {
        setIsErrorDescriptionModalOpened(errorReasonSelected !== undefined);
    }, [errorReasonSelected])

    const buildBadge = (row: RobotExecutions): JSX.Element => {
        return (
            <Tooltip title={buildTooltipContent(row.custom)} arrow>
                <>
                    <span className={`badge badge-light-${getStatusColor(row.status)}`}>
                        <i className={`${getTimeLineBadge(row.status)} me-2`}></i>
                        {getStatusText(row.status, t)}
                    </span>
                    {shouldRenderSuspectDialogButton(row) &&
                        <button
                            type="button"
                            className="btn btn-icon btn-link"
                            onClick={() => openSuspectDescriptionModal(row)}
                        >
                            <span>
                                {row.suspectReason == null && <i className="bi bi-pencil-fill fs-7"></i>}
                                {row.suspectReason != null && <i className="bi bi-eye-fill fs-7"></i>}
                            </span>
                        </button>
                    }
                    {shouldRenderErrorDialogButton(row) &&
                        <button
                            type="button"
                            className="btn btn-icon btn-link"
                            onClick={() => openErrorDescriptionModal(row.id, row.errorReason)}
                        >
                            <span>
                                <i className="bi bi-pencil-fill fs-7"></i>
                            </span>
                        </button>
                    }

                </>
            </Tooltip>
        );
    };

    const columns: TableColumn<RobotExecutions>[] = [
        {
            name: `${t('robotExecutions.tableHeaders.stepName')}`,
            cell: (row) => (
                <Tooltip title={<span className='fs-8'>{t('robotExecutions.tableHeaders.processName')}: {row.processName}</span>} arrow>
                    <span>{row.stepName}</span>
                </Tooltip>
            ),
            sortable: false,
            width: '200px'
        },
        {
            name: `${t('robotExecutions.tableHeaders.machineName')}`,
            cell: (row) => row.machineIsUp
                ? (
                    <Tooltip title={t('generalMessages.connected')}>
                        <span>
                            <i className='fa-solid fa-link text-success me-2' />
                            {row.machineName}
                        </span>
                    </Tooltip>
                ) : (
                    <Tooltip title={t('generalMessages.disconnected')}>
                        <div>
                            <i className='fa-solid fa-link-slash text-danger me-2' />
                            {row.machineName}
                        </div>
                    </Tooltip>
                ),
            sortable: false
        },
        {
            name: `${t("robotExecutions.tableHeaders.departmentName")}`,
            cell: (row) => row.departments?.map((dp, index) => <span className="badge badge-light-primary fs-8 fw-bold my-2 me-3" key={index}>{dp}</span>),
            sortable: false,
            width: '320px',
        },
        {
            name: `${t('robotExecutions.tableHeaders.start')}`,
            selector: (row) => formatLocalizedDateTime(row.start, 'llll') || '',
            sortable: true,
            sortField: 'start',
            width: '250px'
        },
        {
            name: `${t('robotExecutions.tableHeaders.end')}`,
            selector: (row) => row.end ? formatLocalizedDateTime(row.end, 'lll') || '-' : '-',
            sortable: true,
            sortField: 'end',
            width: '220px'
        },
        {
            name: `${t('robotExecutions.tableHeaders.reason')}`,
            cell: (row) => row.errorReason ?
                (<Tooltip title={row.errorReason.reason}>
                    <span>
                        {row.errorReason.name}
                    </span>
                </Tooltip>)
                :
                <span></span>,
            sortable: false,
            width: '220px'
        },
        {
            name: `${t('robotExecutions.tableHeaders.status')}`,
            cell: (row) => buildBadge(row),
            width: '150px'
        },
    ];


    const handleRowsPerPageChange = async (newRowsPerPage: number) => {
        setIsLoading(true);
        setTableData([]);
        setSorting({
            ...sorting,
            limit: newRowsPerPage
        });
    }

    const handlePageChange = (newPage: number) => {
        setIsLoading(true);
        setSorting({
            ...sorting,
            page: newPage - 1
        });
    };

    const handleListSort = (selectedColumn: TableColumn<RobotExecutions>, sortDirection: SortOrder) => {
        setIsLoading(true);
        setTableData([]);
        setSorting({
            ...sorting,
            sort: selectedColumn.sortField,
            order: sortDirection
        });
    }

    return (
        <MasterLayout>
            <ToolbarLayout>
                <ToolbarLayoutLeft />
                <ToolbarLayoutRight>
                    <div className='d-flex align-items-center gap-2'>
                        <RobotExecutionsFilterDropdown
                            filters={filter}
                            filterActions={filterActions}
                        />
                    </div>
                </ToolbarLayoutRight>
            </ToolbarLayout>
            <MainLayout>
                <div className='card card-flush'>
                    <div className='card-body'>
                        <DataTable
                            title={false}
                            persistTableHead
                            columns={columns}
                            data={tableData}
                            noDataComponent={tableHelper.NoDataComponent()}
                            dense
                            highlightOnHover
                            pagination
                            paginationServer
                            paginationComponentOptions={tableHelper.PaginationComponentOptions()}
                            paginationPerPage={tableHelper.defaultRowsPerPage}
                            paginationRowsPerPageOptions={tableHelper.paginationRowsPerPageOptions}
                            paginationTotalRows={totalRows}
                            onChangeRowsPerPage={handleRowsPerPageChange}
                            onChangePage={handlePageChange}
                            onSort={handleListSort}
                            sortServer
                            progressPending={isLoading}
                            progressComponent={tableHelper.Loader()}
                            customStyles={itLabTableStyle}
                        />
                    </div>
                </div>
                <ErrorDescriptionModal agenteLogErrorReason={errorReasonSelected} onClose={closeDescriptionModal} show={isErrorDescriptionModalOpened} />
                <SuspectDescriptionModal agentLog={suspectLogSelect} onClose={closeDescriptionModal} />
            </MainLayout>
        </MasterLayout>
    )
}